import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Box, Card, FieldData, SideDrawer, TextField, useForm, withStyles } from 'vopay';
import { Button } from '#shared/components/UI';
import useEditSubAccount from '#shared/hooks/useApi/useEditSubAccount';
import { useToast } from '#shared/providers/Toast';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import styles from './EditSubAccountDrawer.module.scss';

// Ported from client-dashboard's SubAccountsList EditSubAccountDrawer (ID-13193) as a 1-1 move. Only the
// host-bound imports changed: shared's Button/Toast/AlertDialog, useBlockingLoader in place of
// useDataFetchIndicator, and feature-scoped keys for the Global.Buttons / Global.Validations strings
// (shared's bundle has no Global namespace).

const cn = withStyles(styles);

const EDIT_SUB_ACCOUNT_FORM_ID = 'editSubAccountForm';

type EditSubAccountFormValues = {
  legalBusinessName: string;
  emailAddress: string;
};

export type EditableSubAccount = {
  AccountID: string;
  AccountName: string;
  LegalBusinessName: string;
  Email: string;
};

type EditSubAccountDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  subAccount: EditableSubAccount;
  onSaved: () => void | Promise<void>;
};

export default function EditSubAccountDrawer({ isOpen, onClose, subAccount, onSaved }: EditSubAccountDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList.EditDrawer' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tValidations } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList.Validations' });

  const { editSubAccount } = useEditSubAccount();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();

  const initialValues: EditSubAccountFormValues = useMemo(
    () => ({
      legalBusinessName: subAccount.LegalBusinessName,
      emailAddress: subAccount.Email,
    }),
    [subAccount.LegalBusinessName, subAccount.Email],
  );

  const config: FieldData<EditSubAccountFormValues>[] = useMemo(
    () => [
      {
        label: () => t('legalNameLabel', { defaultValue: 'Legal Business Name' }),
        fieldName: 'legalBusinessName',
        getInputState: () => 'required',
        showField: () => true,
        validate: () =>
          Yup.string()
            .trim()
            .required(tValidations('required', { defaultValue: 'This field is required' })),
        renderField: ({ getFormikData }) => (
          <TextField
            variant="outlineShadow"
            placeholder={t('legalNamePlaceholder', { defaultValue: 'Legal business name' })}
            {...getFormikData<string>({})}
          />
        ),
      },
      {
        label: () => t('emailLabel', { defaultValue: 'Email Address' }),
        fieldName: 'emailAddress',
        getInputState: () => 'required',
        showField: () => true,
        validate: () =>
          Yup.string()
            .trim()
            .email(t('invalidEmail', { defaultValue: 'Please enter a valid email address' }))
            .required(tValidations('required', { defaultValue: 'This field is required' })),
        renderField: ({ getFormikData }) => (
          <TextField
            variant="outlineShadow"
            placeholder={t('emailPlaceholder', { defaultValue: 'name@example.com' })}
            {...getFormikData<string>({})}
          />
        ),
      },
    ],
    [t, tValidations],
  );

  const { ctx, renderFormElements } = useForm<EditSubAccountFormValues>({
    config,
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        blockingLoader.show();

        // The API requires both fields on every edit, even if only one changed.
        await editSubAccount({
          SubaccountID: subAccount.AccountID,
          LegalBusinessName: values.legalBusinessName.trim(),
          EmailAddress: values.emailAddress.trim(),
        });

        await onSaved();
        onClose();

        toast.success(
          t('successToast', {
            accountName: subAccount.AccountName,
            defaultValue: '{{accountName}} has been updated',
          }),
        );
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('errorMessage', { defaultValue: 'Could not update sub-account' });

        alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), message);
      } finally {
        blockingLoader.hide();
      }
    },
  });

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Edit Sub-Account' })}
      footer={
        <Box className={cn('footer')}>
          <Button colorScheme="secondary" onClick={onClose}>
            {tButtons('cancel', { defaultValue: 'Cancel' })}
          </Button>

          <Button
            colorScheme="primary"
            disabled={!ctx.isValid || !ctx.hasPendingData}
            form={EDIT_SUB_ACCOUNT_FORM_ID}
            type="submit"
          >
            {tButtons('save', { defaultValue: 'Save' })}
          </Button>
        </Box>
      }
    >
      <form
        id={EDIT_SUB_ACCOUNT_FORM_ID}
        className={cn('body')}
        onSubmit={(e) => {
          e.preventDefault();
          ctx.handleSubmit(e);
        }}
      >
        <Card hoverEffect={false}>
          <Box flx="col" gapSize="16px">
            {renderFormElements({ config })}
          </Box>
        </Card>
      </form>
    </SideDrawer>
  );
}
